import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useMemo, useState } from 'react';
import { noop } from 'ts-essentials';
import { Badge, Box, Button, ButtonGroup, Link } from '@mui/material';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { AdminPanelSettingsRounded, HelpOutline, Map, SimCard, Speed, } from '@mui/icons-material';
import { useShowMapDashboard } from 'console/account/popup-for-pro';
import { useCallbacks, usePartial } from 'shared/hooks/utils';
import { selectMapDashboardActive } from 'pages/dashboard.map/model/selectors';
import { pickDevice } from 'console/redux/main-actions';
import { select_selected_devices_tree_filters } from 'console/redux/selectors';
import { DeviceSearchField } from './search-field';
import { SortableDeviceTree } from './tree';
import { can_manage_groups } from 'console/account/account_tsx';
import { isMobileApp } from 'webui/utils/version';
import { RenderZontNextTreeItem } from './tree/item';
import { RenderZontNextGroupItem } from './tree/item-group';
import { billedSimsWithProblems } from 'console/sim/utils/sim-problem';
import { activateZTHDashboard, selectZthDashboardActive, selectZthDashboardEnabled, } from 'console/dashboards/zth/actions';
import { leaveFromDashboard, navigateToDashboard } from 'console/dashboards/z3k/actions';
import { is_dashboard_selected } from 'console/dashboards/z3k/selectors';
import { Header } from './header';
import { DeviceControlButton } from './control-button';
import { selectDevicesFields } from 'entities/device/lib';
import { useHasPaidTariff } from 'console/shared/hooks/helpers';
import { size_of_nodes } from 'console/sortableDeviceTree/utils/node-size';
import { useNavigateTo } from 'shared/hooks/router';
import { activeMapDashboard, exitMapDashboard } from 'pages/dashboard.map/model/store';
import { useKeybind } from 'console/left-menu/model/use-keybind';
import { AddDeviceButton } from 'console/left-menu/add-device';
const required_devices_to_search = 5;
const box_sx = { overflowY: 'hidden' };
const sim_button_sx = {
    borderRadius: '45px',
};
const bottom_button_sx = {
    borderRadius: '50px',
};
const map_button_sx = {
    ml: -2,
    borderRadius: '45px',
    '&:hover': {
        boxShadow: 'none',
    },
    boxShadow: 'none',
    marginLeft: 0,
};
function LeftMenuImpl(props) {
    const { closeMenu } = props;
    const show_map = useShowMapDashboard();
    const show_zth_dashboard = useAppSelector(selectZthDashboardEnabled);
    const is_staff = useAppSelector((state) => state.account.is_staff);
    const account_id = useAppSelector((state) => state.account.id);
    const [search, setSearch] = useState('');
    const onMapDashboardClick = useCallbacks([
        () => console.log('onMapDashboardClick'),
        useAction(activeMapDashboard),
        closeMenu,
    ]);
    const onOpenSimListDialog = useCallbacks([useNavigateTo('/sim/list/'), closeMenu]);
    const openHelpDialog = useNavigateTo('support/main');
    const onHelpClick = useCallbacks([openHelpDialog, closeMenu]);
    const can_take_changes_groups = useAppSelector(can_manage_groups);
    const selected_state_filters = useAppSelector(select_selected_devices_tree_filters) ?? [];
    const are_filters_selected = useMemo(() => {
        const selected_filters_count = selected_state_filters.length;
        const are_filters_selected = selected_filters_count > 0;
        return are_filters_selected;
    }, [selected_state_filters]);
    const map_selected = useAppSelector(selectMapDashboardActive);
    const zth_dashboard_selected = useAppSelector(selectZthDashboardActive);
    const dashboard_selected = useAppSelector(is_dashboard_selected);
    const have_unread_messages = useAppSelector((state) => state.support_chat.have_unread_messages);
    const user_devices = useAppSelector(selectDevicesFields(['id'])) ?? [];
    const onZTHDashboardClick = useCallbacks([
        useAction(usePartial(pickDevice, null)),
        useAction(leaveFromDashboard),
        useAction(activateZTHDashboard),
        closeMenu,
    ]);
    const onZ3KDashboardClick = useCallbacks([
        useAction(usePartial(pickDevice, null)),
        useAction(navigateToDashboard),
        useAction(exitMapDashboard),
        closeMenu,
    ]);
    const show_search = useMemo(() => user_devices.length > required_devices_to_search, [user_devices]);
    const billed_problem_sims_count = useAppSelector((state) => billedSimsWithProblems(state).length);
    const has_paid_tariff = useHasPaidTariff();
    const openKeyboardShortcuts = useNavigateTo('/keyboard_shortcuts');
    const openDebugMeDialog = useNavigateTo('/debugme');
    useKeybind({
        onOpenSimListDialog,
        onHelpClick,
        onMapDashboardClick,
        onZTHDashboardClick,
        onZ3KDashboardClick,
        is_staff,
        account_id,
        show_map,
        show_zth_dashboard,
        has_paid_tariff,
        openKeyboardShortcuts,
        openDebugMeDialog,
    });
    return (_jsxs(Box, { className: "MobileLeftMenu", display: "grid", gridTemplateRows: `min-content 5fr 135px`, height: "100%", sx: box_sx, children: [_jsxs(Box, { display: "flex", flexDirection: "column", p: 1, gap: 1, children: [_jsx(Header, {}), _jsxs(Box, { display: "flex", flexDirection: "column", rowGap: 1, children: [_jsxs(ButtonGroup, { fullWidth: true, color: "info", children: [ENABLE_SIM && (_jsx(Button, { startIcon: _jsx(SimCard, {}), onClick: onOpenSimListDialog, color: "info", fullWidth: true, sx: sim_button_sx, "data-tooltip": "sims-button", children: _jsx(Badge, { badgeContent: billed_problem_sims_count, color: "error", children: "\u0421\u0438\u043C\u2011\u043A\u0430\u0440\u0442\u044B" }) })), show_map && (
                                    //@ts-ignore
                                    _jsx(Button, { className: "big-map", startIcon: _jsx(Map, {}), onClick: onMapDashboardClick, variant: map_selected ? 'contained' : 'outlined', color: "success", sx: map_button_sx, children: "\u041A\u0430\u0440\u0442\u0430" }))] }), show_zth_dashboard && (_jsx(Button, { onClick: onZTHDashboardClick, variant: zth_dashboard_selected ? 'contained' : 'outlined', sx: { borderRadius: 12 }, startIcon: _jsx(Speed, {}), children: "\u0421\u0432\u043E\u0434\u043A\u0430" })), has_paid_tariff && (
                            //@ts-ignore
                            _jsx(Button, { onClick: onZ3KDashboardClick, variant: dashboard_selected ? 'contained' : 'outlined', sx: { borderRadius: 12 }, startIcon: _jsx(Speed, {}), children: "\u0421\u0432\u043E\u0434\u043A\u0430" }))] }), show_search && (_jsx(DeviceSearchField, { search: search, setSearch: setSearch }))] }), _jsx(SortableDeviceTree, { search: search, sizeOfNodes: size_of_nodes.normal, onSelectedDevice: closeMenu, drag_state: noop, are_filters_selected: are_filters_selected, can_manage_groups: can_take_changes_groups, can_manage_devices: true, GroupComponent: RenderZontNextGroupItem, ItemComponent: RenderZontNextTreeItem }), _jsxs(Box, { pb: 0.5, px: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 1, children: [!is_staff && (_jsx(AddDeviceButton, { closeMenu: closeMenu, sx: bottom_button_sx })), _jsx(DeviceControlButton, { closeMenu: closeMenu }), _jsx(Badge, { badgeContent: have_unread_messages ? ' ' : 0, variant: "dot", color: "error", children: _jsx(Button, { color: "error", variant: "outlined", startIcon: _jsx(HelpOutline, {}), onClick: onHelpClick, fullWidth: true, sx: bottom_button_sx, "data-tooltip": "support-button", children: "\u041F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0430" }) }), is_staff && !isMobileApp() && (_jsx(Button, { color: "primary", variant: "outlined", startIcon: _jsx(AdminPanelSettingsRounded, {}), fullWidth: true, sx: bottom_button_sx, component: Link, href: `${window.ZONT_URL.cp ?? 'cp'}/users/${account_id}`, children: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C\u0441\u044F \u0432 \u0430\u0434\u043C\u0438\u043D\u043A\u0443" }))] })] }));
}
export const LeftMenu = memo(LeftMenuImpl);
